import React, { useState, useEffect, Suspense } from "react";
import { useGame } from "context/gameContext";
import { useExplore } from "context/exploreContext";
import { useTranslation } from "react-i18next";

import {
  where,
  query,
  collection,
  startAfter,
  limit,
  getDocs,
  orderBy,
} from "firebase/firestore";
import SkeletonLoader from "components/skeletonLoader";
import { useFirestore, useFirestoreCollectionData } from "reactfire";
import ExploreFilter from "views/explore/exploreContent/filters";
import { useUser } from "reactfire";
import {
  IonContent,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonList,
  IonItem,
  IonAvatar,
  IonLabel,
  IonButton,
} from "@ionic/react";
const LeagueCard = React.lazy(() => import("components/leagueCard"));
const ExploreContent = () => {
  const { t } = useTranslation();

  const { data: user } = useUser();
  const {
    state: { userGameData },
  } = useGame();
  const {
    state: { league, leagues, gameType, gameTypes, gameRule, gameRules },
    dispatch,
  } = useExplore();

  const [lastVisible, setLastVisible] = useState(null);
  const [leagueList, setLeagueList] = useState([]);
  const [isFetching, setIsFetching] = useState(false);

  const PAGE_SIZE = 50; // Number of items to fetch per page

  const firestore = useFirestore();

  const tournamentsCollection = collection(firestore, "leagues");
  const tournamentsQuery = query(
    tournamentsCollection,
    where("visible", "==", true)
  );

  const { status: tournamentsStatus, data: tournaments } =
    useFirestoreCollectionData(tournamentsQuery, {
      idField: "id",
    });
  useEffect(() => {
    if (tournaments) {
      const data2 = {
        id: 0,
        title: t("lobby.filter.all"),
        ref: null,
      };
      const data = tournaments
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((doc, id) => ({
          id: id + 1,
          title: doc.name,
          ref: doc.id,
          flag: doc.flag,
        }));
      data.unshift(data2);

      dispatch({ type: "setLeagues", value: data });
    }
  }, [tournaments]);

  /*   useEffect(() => {
    fetchData(true);
  }, []); */

  useEffect(() => {
    setIsFetching(true);
    setLeagueList([]);
    setLastVisible(undefined);
    fetchData(true).then(() => {
      setIsFetching(false);
    });
  }, [league, gameType, gameRule]);

  const fetchData = async (initial) => {
    let q = query(
      collection(firestore, "userTournaments"),
      orderBy("highlighted", "desc"),
      orderBy("userCount", "desc"),
      where("settled", "==", false),
      where("publicLeague", "==", true)
    );

    if (leagues[league]?.ref) {
      q = query(q, where("leagueRef", "==", leagues[league]?.ref));
    }

    if (gameTypes[gameType]?.ref !== undefined) {
      q = query(q, where("gameType", "==", gameTypes[gameType]?.ref));
    }

    if (gameRules[gameRule]?.ref !== undefined) {
      q = query(q, where("gameRule", "in", gameRules[gameRule]?.ref));
    }

    q = query(
      q,
      ...(!initial && lastVisible?.exists() ? [startAfter(lastVisible)] : [])
    );

    q = query(q, limit(PAGE_SIZE));

    const querySnapshot = await getDocs(q);

    if (querySnapshot.docs.length === 0) {
      return;
    }

    const filteredData = querySnapshot.docs
      .map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }))
      .filter((item) => {
        const userHasJoined = item?.users?.includes(user.uid);
        const isPastDeadline =
          new Date(item?.deadline?.seconds * 1000) < new Date();

        if (
          userHasJoined ||
          (isPastDeadline && item?.gameType === 1) ||
          !item?.visible
        ) {
          return false;
        }
        return true;
      });

    setLastVisible(querySnapshot.docs[querySnapshot.docs.length - 1]);
    setLeagueList((prevData) =>
      initial ? filteredData : [...prevData, ...filteredData]
    );
  };

  const fetchMoreListItems = () => {
    fetchData(false);
  };

  async function callClearFilter(e) {
    e.preventDefault();
    dispatch({ type: "setClearFilter" });
  }

  return (
    <IonContent className="mainPage ">
      <div className="max-w-screen-md mx-auto">
        <ExploreFilter />
        <IonList
          mode="ios"
          className="no-lines mx-2 grid grid-cols-1 gap-3 sm:grid-cols-2 customItem transparentList accordion pt-2"
        >
          {!userGameData || tournamentsStatus == "loading" || isFetching ? (
            <SkeletonLoader
              type={"tournamentCard"}
              amount={3}
              variant={"medium"}
            />
          ) : leagueList?.length > 0 ? (
            leagueList.map((item, index) => (
              <IonItem
                key={index}
                className="ionInputNoInnerPadding ion-no-padding rounded-md"
                lines="none"
              >
                <LeagueCard
                  leagueData={item}
                  userGameData={userGameData}
                  key={item.id}
                  joined={false}
                  slim={true}
                />
              </IonItem>
            ))
          ) : (
            <div className="flex flex-col text-lg font-light bg-white/50 px-20 rounded-md  text-center py-10 justify-center m-2 space-y-2">
              <div className="pb-6 text-center">
                {t("lobby.empty.exploretitle")}
                {/* No leagues found with the selected filters  */}
              </div>
              <IonButton
                mode="ios"
                fill="outline"
                color="dark"
                className="m-0 h-10 customButtonNew"
                onClick={(e) => callClearFilter(e)}
              >
                <IonLabel className="text-sm">
                  {t("lobby.empty.clear")}
                </IonLabel>
              </IonButton>
            </div>
          )}
        </IonList>
        <IonInfiniteScroll
          threshold="100px"
          onIonInfinite={(ev) => {
            fetchMoreListItems();
            setTimeout(() => ev.target.complete(), 500);
          }}
        >
          <IonInfiniteScrollContent></IonInfiniteScrollContent>
        </IonInfiniteScroll>
      </div>
    </IonContent>
  );
};

export default ExploreContent;
